import {
  basename,
  dirname,
  extname,
  titleFromPath,
  type FileKind,
  type VaultFileEntry,
} from '@rbv/shared';

/** Display kind for tree icons: `.base` files get their own icon/view. */
export type TreeFileKind = 'note' | 'canvas' | 'base' | 'file';

export interface TreeFile {
  id: string;
  path: string;
  /** Display name: title (no extension) for notes/canvas/base, basename for attachments. */
  name: string;
  kind: TreeFileKind;
}

export interface TreeFolder {
  /** Last path segment; '' for the root. */
  name: string;
  /** Full folder path; '' for the root. */
  path: string;
  folders: TreeFolder[];
  files: TreeFile[];
}

export function treeFileKind(path: string, kind: FileKind): TreeFileKind {
  if (extname(path) === '.base') return 'base';
  return kind;
}

/**
 * Derive the folder hierarchy from vault file paths (deleted entries are
 * skipped). `extraFolders` are ephemeral empty folders kept in UI state.
 * Folders and files are each sorted alphabetically (folders listed first by
 * the renderer).
 */
export function buildTree(
  files: Iterable<[string, VaultFileEntry]>,
  extraFolders: Iterable<string> = [],
): TreeFolder {
  const root: TreeFolder = { name: '', path: '', folders: [], files: [] };
  const byPath = new Map<string, TreeFolder>([['', root]]);

  const folderAt = (path: string): TreeFolder => {
    const existing = byPath.get(path);
    if (existing) return existing;
    const parent = folderAt(dirname(path));
    const folder: TreeFolder = { name: basename(path), path, folders: [], files: [] };
    parent.folders.push(folder);
    byPath.set(path, folder);
    return folder;
  };

  for (const p of extraFolders) if (p) folderAt(p);

  for (const [id, e] of files) {
    if (e.deleted) continue;
    const kind = treeFileKind(e.path, e.kind);
    folderAt(dirname(e.path)).files.push({
      id,
      path: e.path,
      name: kind === 'file' ? basename(e.path) : titleFromPath(e.path),
      kind,
    });
  }

  const sortRec = (f: TreeFolder) => {
    f.folders.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
    f.files.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
    for (const c of f.folders) sortRec(c);
  };
  sortRec(root);
  return root;
}

/** Every folder path in the tree (depth-first, root '' excluded). */
export function allFolderPaths(root: TreeFolder): string[] {
  const out: string[] = [];
  const walk = (f: TreeFolder) => {
    for (const c of f.folders) {
      out.push(c.path);
      walk(c);
    }
  };
  walk(root);
  return out;
}

/** First free `Stem.ext` / `Stem N.ext` path inside `dir` (case-insensitive). */
export function untitledPath(
  files: Iterable<[string, VaultFileEntry]>,
  dir: string,
  stem: string,
  ext: string,
): string {
  const taken = new Set<string>();
  for (const [, e] of files) if (!e.deleted) taken.add(e.path.toLowerCase());
  const prefix = dir ? `${dir}/` : '';
  for (let n = 0; ; n++) {
    const candidate = `${prefix}${stem}${n === 0 ? '' : ` ${n}`}${ext}`;
    if (!taken.has(candidate.toLowerCase())) return candidate;
  }
}

/** First free folder name `Stem` / `Stem N` inside `dir` among `existing` folder paths. */
export function untitledFolder(
  existing: Iterable<string>,
  dir: string,
  stem = 'New folder',
): string {
  const taken = new Set<string>();
  for (const p of existing) taken.add(p.toLowerCase());
  const prefix = dir ? `${dir}/` : '';
  for (let n = 0; ; n++) {
    const candidate = `${prefix}${stem}${n === 0 ? '' : ` ${n}`}`;
    if (!taken.has(candidate.toLowerCase())) return candidate;
  }
}
